import {
  Component,
  Inject,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiDataSource } from '../api-data-source';

@Component({
  selector: 'app-file-manager',
  templateUrl: './file-manager.component.html',
  styleUrls: ['./file-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class FileManagerComponent implements OnInit {
  dataSource: FileManagerDataSource = new FileManagerDataSource(
    this.app,
    this.api
  );

  listColumns: string[] = [
    'id',
    'thumbnail',
    'name',
    'date_created',
    'actions'
  ];

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  selectedFiles: File[] = [];

  parentObjectType: string = '';
  parentObjectId: number = 0;
  parentRemove: boolean = false;
  parentUpload: boolean = false;
  getSelectedFiles: boolean = false;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private dialogRef: MatDialogRef<FileManagerComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {}

  ngOnInit(): void {
    if (this.dialogData['parentObjectType']) {
      this.parentObjectType = this.dialogData['parentObjectType'];
    }

    if (this.dialogData['parentObjectId']) {
      this.parentObjectId = this.dialogData['parentObjectId'];
    }

    if (this.dialogData['parentRemove']) {
      this.parentRemove = this.dialogData['parentRemove'];
    }

    if (this.dialogData['parentUpload']) {
      this.parentUpload = this.dialogData['parentUpload'];
    }

    if (this.dialogData['getSelectedFiles']) {
      this.getSelectedFiles = this.dialogData['getSelectedFiles'];
    }

    if (
      this.dialogData['parentObjectType'] &&
      this.dialogData['parentObjectId']
    ) {
      this.dataSource.parentObjectType = this.parentObjectType;
      this.dataSource.parentObjectId = this.parentObjectId;
      this.dataSource.getData();
    }
  }

  ngAfterViewInit(): void {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator.pageIndex = paginator['pageIndex'];
      this.paginator.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap((sorter) => {
          this.dataSource.order_by = sorter['active'];
          this.dataSource.order = sorter['direction'];
          this.dataSource.getData();
        })
      )
      .subscribe();
  }

  /**
   * Add the files to the selected files array and upload it.
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if (files && files.length > 0) {
      // The browse button already holds back oversized files, so this normally passes everything
      // through. Repeated here because this method is public and reachable from callers that did not
      // come through that button, and an oversized file is worth stopping before it is sent.
      const { allowed, rejected } = this.utils.checkFileSizes(files);

      if (rejected.length > 0) {
        this.utils.showModal('File Too Large', this.utils.getFilesTooLargeMessage(rejected));
      }

      if (allowed.length === 0) {
        return;
      }

      this.selectedFiles.push(...allowed);
      this.onUploadSelectedFiles();
    }
  }

  /**
   * Upload the selected files to the relevant object.
   */
  onUploadSelectedFiles(): void {
    if (this.selectedFiles.length > 0) {
      if (this.parentUpload) {
        this.dialogRef.close(this.selectedFiles);
      } else {
        // Make an API request to upload the files self.
        this.api
          .makeUploadRequest(
            `v2/file-manager/${this.parentObjectType}/${this.parentObjectId}`,
            this.selectedFiles
          )
          .then(
            (response): void => {
              this.utils.showToast('Your files successfully uploaded.');
              this.dataSource.getData();
              if (this.getSelectedFiles) {
                this.dialogRef.close([...this.selectedFiles]);
              }
            }
          )
          .catch(
            (error): void => {
              // Without this the rejection was an unhandled promise: the list silently refreshed
              // empty and the upload looked like it had succeeded. ErrorInterceptorService
              // already reports the transport, 413 and 5xx cases, and handleAPIErrors() repeats the
              // same wording for those, so the two cannot contradict each other. This call is what
              // surfaces the API's own validation message, such as the per file 24MB and unsupported
              // file type rejections, which arrive as a 422 carrying only a `message`.
              this.utils.handleAPIErrors(error);
            }
          )
          .finally(
            (): void => {
              this.selectedFiles.length = 0;
            }
          );
      }
    }
  }

  /**
   *
   * @param {number} fileId - The ID of the file to be removed
   * @param {any} event - The event that triggered the removal action
   * @return {void} - This method does not return any value
   */
  onRemoveUploadedFile(fileId: number, event: any): void {
    if (this.parentObjectType && this.parentObjectId && fileId) {
      this.utils.showQuickActions(
        event.target,
        'Are you sure you want to remove this uploaded file?',
        [
          {
            text: 'Yes',
            handler: (): void => {
              if (this.parentRemove) {
                this.dialogRef.close(fileId);
              } else {
                this.api
                  .makeRequest(
                    'delete',
                    `v2/file-manager/${this.parentObjectType}/${
                      this.parentObjectId
                    }/${fileId}`
                  )
                  .then(
                    (): void => {
                      this.dataSource.getData();
                      this.utils.showToast(
                        'The file was successfully removed.'
                      );
                    }
                  );
              }
            }
          },
          {
            text: 'No',
            handler: (): void => {}
          }
        ]
      );
    } else {
      this.utils.showToast(
        'Unable to remove the file. Please refresh the page and try again.'
      );
    }
  }

  /**
   * Opens the provided URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void}
   */
  onOpenUrl(url: string): void {
    if (!url) {
      return;
    }
    this.utils.openUrl(url);
  }

  /**
   * Displays a toast message informing the user that the URL has been copied to the clipboard.
   *
   * @return {void}
   */
  onCopiedURL(): void {
    this.utils.showToast('The URL is copied to your clipboard.');
  }

  /**
   * Checks if the given filename is an image based on common image file extensions.
   *
   * @param name - The filename to be checked for image extension.
   * @return - An array of matches if the filename has a valid image extension (jpg, jpeg, png, gif, bmp),
   *           or null if the filename does not have a valid image extension.
   */
  isImage(name: string): RegExpMatchArray {
    return name && name.match(/.(jpg|jpeg|png|gif|bmp)/i);
  }

  onClose() {
    this.dialogRef.close();
  }
}

export class FileManagerDataSource extends ApiDataSource {
  order_by: string = 'date_created';
  order: string = 'desc';

  parentObjectType: string;
  parentObjectId: number;

  getData(resetOffset: boolean = false): void {
    this.makeRequest(
      `v2/file-manager/${this.parentObjectType}/${this.parentObjectId}`,
      resetOffset
    );
  }
}
